import { useCallback, useEffect, useRef, useState } from 'react';
import {
  COOKIES_CONFIG,
  MAINTENANCE_AGENT_OCTANE_FLAG,
  resolveMaintenanceAgentUrl,
  type MaintenanceAgentBackend,
} from '@belong/common';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import {
  Client,
  type Thread,
  type Metadata,
  type DefaultValues,
} from '@langchain/langgraph-sdk';
import { parseCookies } from 'nookies';

const ConversationStatusEnum = {
  COMPLETED_BY_TROUBLESHOOTING: 'completed_by_troubleshooting',
  COMPLETED_BY_TICKET: 'completed_by_ticket',
  COMPLETED_BY_HUMAN_INTERACTION: 'completed_by_human_interaction',
  INCOMPLETE: 'incomplete',
} as const;

type ConversationStatus =
  (typeof ConversationStatusEnum)[keyof typeof ConversationStatusEnum];

interface ThreadMessage {
  id: string;
  type: 'human' | 'ai';
  content: (
    | string
    | { type: 'text'; text: string }
    | { type: 'image_url'; image_url: string | { url: string } }
  )[];
}

interface ThreadValuesWithMessages {
  messages?: ThreadMessage[];
  conversationStatus: ConversationStatus;
  ticketId?: string;
}

export type MaintenanceThread = {
  id: string;
  backend: MaintenanceAgentBackend;
  lastMessagePreview: string;
  createdAt: string;
  metadata: Metadata;
  summary?: string;
  ended: boolean;
  ticketId?: string;
};

type ThreadValues = DefaultValues & {
  summary?: string;
  conversationStatus: ConversationStatus;
  ticketId?: string;
};

export const useMaintenanceAgentHistory = ({
  userId,
  enabled,
}: {
  userId: string;
  enabled: boolean;
}) => {
  const {
    treatments,
    isReady: flagReady,
    hasTimedout,
  } = useSplitTreatments({
    names: [MAINTENANCE_AGENT_OCTANE_FLAG],
    splitKey: userId,
  });
  const backend: MaintenanceAgentBackend =
    flagReady && treatments[MAINTENANCE_AGENT_OCTANE_FLAG]?.treatment === 'on'
      ? 'octane'
      : 'langgraph';
  const requestVersion = useRef(0);
  const [threads, setThreads] = useState<MaintenanceThread[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchThreads = useCallback(async () => {
    const request = ++requestVersion.current;
    if (!enabled || (!flagReady && !hasTimedout)) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetchThreadsByUser(userId, backend);
      if (request === requestVersion.current) setThreads(response);
    } catch (e) {
      console.log(e);
    } finally {
      if (request === requestVersion.current) setIsLoading(false);
    }
  }, [enabled, userId, backend, flagReady, hasTimedout]);

  useEffect(() => {
    setThreads(null);
    fetchThreads();
    return () => {
      requestVersion.current++;
    };
  }, [userId, enabled, fetchThreads]);

  return {
    threads,
    isLoading: isLoading || (enabled && !flagReady && !hasTimedout),
    refetch: fetchThreads,
  };
};

function buildAgentClient(backend: MaintenanceAgentBackend) {
  const cookies = parseCookies();
  return new Client({
    apiUrl: resolveMaintenanceAgentUrl(
      backend,
      process.env.NEXT_PUBLIC_AGENT_URL,
      process.env.NEXT_PUBLIC_OCTANE_MAINTENANCE_AGENT_URL,
    ),
    defaultHeaders: {
      Authorization: `Bearer ${cookies[COOKIES_CONFIG.JWT_TOKEN.name]}`,
    },
  });
}

export async function updateThread(
  threadId: string,
  assistantId: string,
  backend: MaintenanceAgentBackend,
) {
  await buildAgentClient(backend).runs.create(threadId, assistantId, {
    command: { goto: 'close_conversation' },
  });
}

async function fetchThreadsByUser(
  userId: string,
  backend: MaintenanceAgentBackend,
): Promise<MaintenanceThread[]> {
  try {
    const threads: Thread<ThreadValues>[] = await buildAgentClient(
      backend,
    ).threads.search({
      metadata: { owner: userId },
      sortBy: 'updated_at',
      sortOrder: 'desc',
      limit: 100,
    });

    const openThreads = threads.filter(
      (thread) =>
        thread.values &&
        thread.values.conversationStatus &&
        thread.values.conversationStatus === ConversationStatusEnum.INCOMPLETE,
    );

    const threadList = openThreads.map((thread: Thread<ThreadValues>) => {
      let lastMessagePreview: string = '';

      const values = thread.values as ThreadValuesWithMessages;

      if (
        values &&
        Array.isArray(values.messages) &&
        values.messages.length > 0
      ) {
        // Find the last human message
        const lastHumanMessage = values.messages
          .slice()
          .reverse()
          .find((message) => message.type === 'human');

        if (lastHumanMessage) {
          if (typeof lastHumanMessage.content === 'string') {
            lastMessagePreview = stripMarkdown(lastHumanMessage.content);
          } else if (Array.isArray(lastHumanMessage.content)) {
            const textContent = lastHumanMessage.content.find(
              (item) => typeof item === 'object' && item.type === 'text',
            )?.text;
            lastMessagePreview = stripMarkdown(textContent || 'New Chat');
          }
        }
      }

      return {
        id: thread.thread_id,
        backend,
        createdAt: thread.updated_at || thread.created_at,
        lastMessagePreview,
        metadata: thread.metadata,
        summary: thread.values.summary,
        ended: false,
        ticketId: thread.values.ticketId,
      };
    });

    return threadList.sort((a, b) => {
      // First sort by ended status (not ended first, then ended)
      if (a.ended !== b.ended) {
        return a.ended ? 1 : -1;
      }
      // If both have same ended status, sort by creation date (newest first)
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  } catch (error) {
    console.error('Failed to fetch threads from LangGraph:', error);
    throw error;
  }
}

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1') // Bold
    .replace(/\*(.*?)\*/g, '$1') // Italic
    .replace(/`(.*?)`/g, '$1') // Inline code
    .replace(/```[\s\S]*?```/g, '') // Code blocks
    .replace(/#{1,6}\s+(.*)/g, '$1') // Headers
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Links
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1') // Images
    .replace(/^\s*[-*+]\s+/gm, '') // List items
    .replace(/^\s*\d+\.\s+/gm, '') // Numbered lists
    .replace(/>\s*(.*)/g, '$1') // Blockquotes
    .replace(/\n\s*\n/g, ' ') // Multiple newlines to single space
    .replace(/\s+/g, ' ') // Multiple spaces to single space
    .trim();
}
